<div class="page-container">
  <app-card class="scrollable-content">
    <app-card-header>
      <app-card-title>
        <div class="error-header">
          <app-icon name="alert-triangle" [size]="48" color="var(--fg-danger)" />
          <h1>{{ 'browserError.title' | translate }}</h1>
        </div>
      </app-card-title>
    </app-card-header>

    <app-card-content>
      <!-- WASM Out of Memory Error -->
      @if (isWasmOom) {
      <div class="error-section">
        <h2>{{ 'browserError.wasm.title' | translate }}</h2>
        <p class="error-description">
          {{ 'browserError.wasm.description' | translate:{ required: (errorData.requiredMemory || 512).toString() } }}
        </p>

        @if (errorData.availableMemory) {
        <div class="memory-info">
          <div class="memory-stat">
            <span class="label">{{ 'browserError.wasm.available' | translate }}</span>
            <span class="value">{{ errorData.availableMemory.toFixed(0) }} MB</span>
          </div>
          <div class="memory-stat">
            <span class="label">{{ 'browserError.wasm.required' | translate }}</span>
            <span class="value">{{ errorData.requiredMemory || 512 }} MB</span>
          </div>
          @if (memoryShortfall) {
          <div class="memory-stat shortfall">
            <span class="label">{{ 'browserError.wasm.shortfall' | translate }}</span>
            <span class="value">{{ memoryShortfall.toFixed(0) }} MB</span>
          </div>
          }
        </div>
        }
      </div>

      <!-- Brave-specific guidance -->
      @if (isBrave) {
      <div class="solutions brave-solutions">
        <h3>{{ 'browserError.brave.title' | translate }}</h3>
        <p class="brave-note">
          <app-icon name="info" color="var(--fg-warning)" />
          {{ 'browserError.brave.note' | translate }}
        </p>

        <ol class="solution-list">
          <li>
            <strong>{{ 'browserError.brave.solution1.title' | translate }}</strong>
            <p>{{ 'browserError.brave.solution1.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.brave.solution2.title' | translate }}</strong>
            <p>{{ 'browserError.brave.solution2.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.brave.solution3.title' | translate }}</strong>
            <p>{{ 'browserError.brave.solution3.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.brave.solution4.title' | translate }}</strong>
            <p>{{ 'browserError.brave.solution4.description' | translate }}</p>
          </li>
        </ol>
      </div>
      }

      <!-- Generic browser guidance -->
      @if (!isBrave) {
      <div class="solutions">
        <h3>{{ 'browserError.generic.title' | translate }}</h3>
        <ol class="solution-list">
          <li>
            <strong>{{ 'browserError.generic.solution1.title' | translate }}</strong>
            <p>{{ 'browserError.generic.solution1.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.generic.solution2.title' | translate }}</strong>
            <p>{{ 'browserError.generic.solution2.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.generic.solution3.title' | translate }}</strong>
            <p>{{ 'browserError.generic.solution3.description' | translate }}</p>
          </li>
          <li>
            <strong>{{ 'browserError.generic.solution4.title' | translate }}</strong>
            <p>{{ 'browserError.generic.solution4.description' | translate }}</p>
          </li>
        </ol>
      </div>
      }

      <!-- Action buttons -->
      <div class="action-buttons">
        <app-button variant="filled" (click)="reloadPage()" data-testid="try-again-button">
          <app-icon name="refresh-cw" />
          {{ 'browserError.tryAgain' | translate }}
        </app-button>

        <app-button variant="outlined" (click)="uploadErrorLogs()" [disabled]="isUploadingLogs()"
          data-testid="upload-logs-button">
          <app-icon [name]="isUploadingLogs() ? 'loader' : 'upload'" />
          {{ isUploadingLogs() ? ('browserError.uploadingLogs' | translate) : ('browserError.uploadLogs' | translate) }}
        </app-button>

        <app-button variant="outlined" (click)="showTechnicalDetails = !showTechnicalDetails"
          data-testid="technical-details-button">
          <app-icon [name]="showTechnicalDetails ? 'chevron-up' : 'chevron-down'" />
          {{ showTechnicalDetails ? ('browserError.hideDetails' | translate) : ('browserError.showDetails' | translate)
          }} {{ 'browserError.technicalDetails' | translate }}
        </app-button>
      </div>

      <!-- Technical details (collapsible) -->
      @if (showTechnicalDetails && errorData.technicalDetails) {
      <div class="technical-details">
        <h4>{{ 'browserError.technicalDetails' | translate }}</h4>
        <pre>{{ errorData.technicalDetails }}</pre>
        <app-button variant="text" size="sm" (click)="copyTechnicalDetails()">
          <app-icon name="copy" />
          {{ 'browserError.copyToClipboard' | translate }}
        </app-button>
      </div>
      }
      }

      <!-- Other error types -->
      @if (!isWasmOom) {
      <div class="error-section">
        <h2>{{ 'browserError.unsupported.title' | translate }}</h2>
        <p class="error-description">
          {{ 'browserError.unsupported.description' | translate }}
        </p>

        <div class="solutions">
          <h3>{{ 'browserError.unsupported.recommendedTitle' | translate }}</h3>
          <ul class="browser-list">
            <li>{{ 'browserError.unsupported.chrome' | translate }}</li>
            <li>{{ 'browserError.unsupported.edge' | translate }}</li>
            <li>{{ 'browserError.unsupported.brave' | translate }}</li>
          </ul>
        </div>

        <div class="action-buttons">
          <app-button variant="filled" (click)="reloadPage()">
            <app-icon name="refresh-cw" />
            {{ 'browserError.tryAgain' | translate }}
          </app-button>

          <app-button variant="outlined" (click)="uploadErrorLogs()" [disabled]="isUploadingLogs()"
            data-testid="upload-logs-button-unsupported">
            <app-icon [name]="isUploadingLogs() ? 'loader' : 'upload'" />
            {{ isUploadingLogs() ? ('browserError.uploadingLogs' | translate) : ('browserError.uploadLogs' | translate)
            }}
          </app-button>
        </div>
      </div>
      }

      <!-- Development mode notice -->
      <div class="dev-notice">
        <app-icon name="info" [size]="16" />
        <p>
          <strong>{{ 'browserError.devNotice' | translate }}</strong>
        </p>
      </div>
    </app-card-content>
  </app-card>
</div>